import {
  DEFAULT_SYMBOL_SIZE,
  MAX_GENERATION_SYMBOLS,
  ProtocolError,
} from '@spectrabus/protocol';

export interface FountainSymbol {
  readonly generationId: number;
  readonly equationId: number;
  readonly sourceCount: number;
  readonly symbolSize: number;
  readonly validBytes: number;
  readonly payload: Uint8Array;
}

export interface FountainAdmission {
  readonly accepted: boolean;
  readonly independent: boolean;
  readonly duplicate: boolean;
  readonly rank: number;
  readonly requiredRank: number;
  readonly reconstructed: Uint8Array | null;
}

interface BasisRow {
  readonly coefficients: Uint8Array;
  readonly payload: Uint8Array;
}

// A legitimate sender needs roughly sourceCount independent admissions plus a
// small dependent tail, so this generous multiple can only be exceeded by a
// flood of distinct equation identifiers. Bounding the tracked set keeps
// decoder memory finite against exactly that flood; an authenticated symbol
// stream should never come near the limit.
const MAX_TRACKED_EQUATIONS_BASE = 128;
const MAX_TRACKED_EQUATIONS_PER_SOURCE = 4;

function xorInto(target: Uint8Array, source: Uint8Array): void {
  for (let index = 0; index < target.length; index += 1) {
    target[index] = (target[index] ?? 0) ^ (source[index] ?? 0);
  }
}

function fnv1a(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function xorshift32(state: number): number {
  let value = state || 0x9e3779b9;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return value >>> 0;
}

export function equationCoefficients(
  seed: string,
  generationId: number,
  equationId: number,
  sourceCount: number,
): Uint8Array {
  if (sourceCount < 1 || sourceCount > MAX_GENERATION_SYMBOLS) {
    throw new ProtocolError('FOUNTAIN_BOUNDS', 'Fountain source count exceeds protocol limits.');
  }
  const coefficients = new Uint8Array(sourceCount);
  if (equationId < sourceCount) {
    coefficients[equationId] = 1;
    return coefficients;
  }

  let state = fnv1a(`${seed}:${generationId}:${equationId}:${sourceCount}`);
  let selected = 0;
  for (let index = 0; index < sourceCount; index += 1) {
    state = xorshift32(state);
    if ((state & 0xffff) < 0x5800) {
      coefficients[index] = 1;
      selected += 1;
    }
  }
  if (selected === 0) {
    coefficients[equationId % sourceCount] = 1;
  }
  return coefficients;
}

export class GenerationEncoder {
  public readonly generationId: number;
  public readonly sourceCount: number;
  public readonly symbolSize: number;
  public readonly validBytes: number;

  readonly #seed: string;
  readonly #sources: readonly Uint8Array[];

  public constructor(
    seed: string,
    generationId: number,
    data: Uint8Array,
    symbolSize = DEFAULT_SYMBOL_SIZE,
  ) {
    if (data.length < 1) {
      throw new ProtocolError('FOUNTAIN_EMPTY', 'A fountain generation requires at least one byte.');
    }
    if (symbolSize < 1) {
      throw new ProtocolError('FOUNTAIN_SYMBOL_SIZE', 'Fountain symbol size must be positive.');
    }
    const sourceCount = Math.ceil(data.length / symbolSize);
    if (sourceCount > MAX_GENERATION_SYMBOLS) {
      throw new ProtocolError('FOUNTAIN_BOUNDS', 'Generation contains too many source symbols.');
    }

    this.#seed = seed;
    this.generationId = generationId;
    this.sourceCount = sourceCount;
    this.symbolSize = symbolSize;
    this.validBytes = data.length;
    this.#sources = Array.from({ length: sourceCount }, (_, index) => {
      const source = new Uint8Array(symbolSize);
      source.set(data.subarray(index * symbolSize, (index + 1) * symbolSize));
      return source;
    });
  }

  public createSymbol(equationId: number): FountainSymbol {
    const coefficients = equationCoefficients(
      this.#seed,
      this.generationId,
      equationId,
      this.sourceCount,
    );
    const payload = new Uint8Array(this.symbolSize);
    for (let index = 0; index < coefficients.length; index += 1) {
      if (coefficients[index] === 1) {
        xorInto(payload, this.#sources[index] ?? new Uint8Array(this.symbolSize));
      }
    }
    return {
      generationId: this.generationId,
      equationId,
      sourceCount: this.sourceCount,
      symbolSize: this.symbolSize,
      validBytes: this.validBytes,
      payload,
    };
  }
}

export class GenerationDecoder {
  public readonly generationId: number;
  public readonly sourceCount: number;
  public readonly symbolSize: number;
  public readonly validBytes: number;

  readonly #seed: string;
  readonly #basis: Array<BasisRow | null>;
  readonly #seenEquationIds = new Set<number>();
  #rank = 0;
  #reconstructed: Uint8Array | null = null;

  public constructor(
    seed: string,
    generationId: number,
    sourceCount: number,
    symbolSize: number,
    validBytes: number,
  ) {
    if (
      sourceCount < 1 ||
      sourceCount > MAX_GENERATION_SYMBOLS ||
      symbolSize < 1 ||
      validBytes < 1 ||
      validBytes > sourceCount * symbolSize
    ) {
      throw new ProtocolError('FOUNTAIN_BOUNDS', 'Decoder generation bounds are invalid.');
    }
    this.#seed = seed;
    this.generationId = generationId;
    this.sourceCount = sourceCount;
    this.symbolSize = symbolSize;
    this.validBytes = validBytes;
    this.#basis = Array.from({ length: sourceCount }, () => null);
  }

  public get rank(): number {
    return this.#rank;
  }

  public get complete(): boolean {
    return this.#reconstructed !== null;
  }

  public add(symbol: FountainSymbol): FountainAdmission {
    if (
      symbol.generationId !== this.generationId ||
      symbol.sourceCount !== this.sourceCount ||
      symbol.symbolSize !== this.symbolSize ||
      symbol.validBytes !== this.validBytes ||
      symbol.payload.length !== this.symbolSize
    ) {
      throw new ProtocolError('FOUNTAIN_MISMATCH', 'Symbol metadata does not match its generation.');
    }
    if (this.#seenEquationIds.has(symbol.equationId)) {
      return this.#result(false, true);
    }
    // A completed generation cannot gain rank, so unseen identifiers are
    // reported as dependent without being tracked; this stops the seen set
    // growing after full rank.
    if (this.#reconstructed !== null) {
      return this.#result(false, false);
    }
    if (
      this.#seenEquationIds.size >=
      MAX_TRACKED_EQUATIONS_BASE + this.sourceCount * MAX_TRACKED_EQUATIONS_PER_SOURCE
    ) {
      throw new ProtocolError(
        'FOUNTAIN_ADMISSION_EXHAUSTED',
        'Generation received far more distinct equations than reconstruction can require.',
      );
    }
    this.#seenEquationIds.add(symbol.equationId);

    const coefficients = equationCoefficients(
      this.#seed,
      this.generationId,
      symbol.equationId,
      this.sourceCount,
    );
    const payload = symbol.payload.slice();

    let pivot = -1;
    for (let index = 0; index < this.sourceCount; index += 1) {
      if (coefficients[index] === 0) continue;
      const row = this.#basis[index];
      if (row !== null && row !== undefined) {
        xorInto(coefficients, row.coefficients);
        xorInto(payload, row.payload);
        continue;
      }
      if (pivot === -1) {
        pivot = index;
      }
    }

    if (pivot === -1) {
      return this.#result(false, false);
    }

    const newRow: BasisRow = { coefficients, payload };
    this.#basis[pivot] = newRow;
    for (let index = 0; index < this.#basis.length; index += 1) {
      if (index === pivot) continue;
      const row = this.#basis[index];
      if (row !== null && row !== undefined && row.coefficients[pivot] === 1) {
        xorInto(row.coefficients, coefficients);
        xorInto(row.payload, payload);
      }
    }

    this.#rank += 1;
    if (this.#rank === this.sourceCount) {
      const joined = new Uint8Array(this.sourceCount * this.symbolSize);
      for (let index = 0; index < this.#basis.length; index += 1) {
        const row = this.#basis[index];
        if (row === null || row === undefined) {
          throw new ProtocolError('FOUNTAIN_INTERNAL', 'Full rank is missing a basis row.');
        }
        joined.set(row.payload, index * this.symbolSize);
      }
      this.#reconstructed = joined.slice(0, this.validBytes);
    }
    return this.#result(true, false);
  }

  public takeReconstructed(): Uint8Array {
    if (this.#reconstructed === null) {
      throw new ProtocolError('FOUNTAIN_INCOMPLETE', 'Generation has not reached full rank.');
    }
    return this.#reconstructed.slice();
  }

  #result(independent: boolean, duplicate: boolean): FountainAdmission {
    return {
      accepted: independent,
      independent,
      duplicate,
      rank: this.#rank,
      requiredRank: this.sourceCount,
      reconstructed: this.#reconstructed?.slice() ?? null,
    };
  }
}
